<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            isAdd
              ? `${$t("actions.add")} ${$tc("ai_analytics_servers.device", 1)}`
              : `${$t("actions.edit")} ${$tc("ai_analytics_servers.device", 1)}`
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>
      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- Camera -->
          <cameras-proxy-select
            v-model="itemData.camera"
            :label="$t('ai_analytics_servers.device')"
            name="camera"
            rules="required"
            :reduce="(val) => val.value"
            class="mb-1"
          />

          <template v-if="serverType === 'fire_smoke'">
            <!-- Fire Threshold -->
            <validation-provider
              #default="validationContext"
              name="fire_threshold"
              :rules="{ required: false, between: { min: 0, max: 1 } }"
            >
              <b-form-group
                :label="`${$t('ai_analytics_servers.fire_threshold')} ${itemData.fire_threshold ? `(${Math.round(itemData.fire_threshold * 100)}%)` : ''}`"
                label-for="fire_threshold"
              >
                <b-form-input
                  id="fire_threshold"
                  v-model="itemData.fire_threshold"
                  type="range"
                  step="0.01"
                  min="0"
                  max="1"
                  :state="getValidationState(validationContext)"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Smoke Threshold -->
            <validation-provider
              #default="validationContext"
              name="smoke_threshold"
              :rules="{ required: false, between: { min: 0, max: 1 } }"
            >
              <b-form-group
                :label="`${$t('ai_analytics_servers.smoke_threshold')} ${itemData.smoke_threshold ? `(${Math.round(itemData.smoke_threshold * 100)}%)` : ''}`"
                label-for="smoke_threshold"
              >
                <b-form-input
                  id="smoke_threshold"
                  v-model="itemData.smoke_threshold"
                  type="range"
                  step="0.01"
                  min="0"
                  max="1"
                  :state="getValidationState(validationContext)"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Use Local Confirmation -->
            <b-form-group
              :label="$t('ai_analytics_servers.use_local_confirmation')"
              label-for="use_local_confirmation"
            >
              <b-form-checkbox
                id="use_local_confirmation"
                v-model="itemData.use_local_confirmation"
                switch
              />
            </b-form-group>
          </template>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInvalidFeedback,
  BButton,
  BFormInput,
  BFormCheckbox,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { ref, watch } from "@vue/composition-api";
import { required } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import CamerasProxySelect from "@/components/CamerasProxySelect.vue";

export default {
  name: "AiAnalyticsDevicesSidenav",
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInvalidFeedback,
    BButton,
    BFormInput,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
    CamerasProxySelect,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    serverId: {
      type: Number,
      required: false,
    },
    serverType: {
      type: String,
      required: false,
      default: null,
    }
  },
  data() {
    return {
      required,
    };
  },
  setup(props, { emit }) {
    const toast = useToast();

    const blankData = {
      camera: null,
      fire_threshold: null,
      smoke_threshold: null,
      use_local_confirmation: false,
    };

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));

    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    // Cargar datos cuando se edita
    watch(
      () => props.itemEdit,
      (newVal) => {
        if (newVal && Object.keys(newVal).length > 0 && !props.isAdd) {
          itemData.value = {
            camera: newVal.id,
            fire_threshold: newVal.fire_threshold,
            smoke_threshold: newVal.smoke_threshold,
            use_local_confirmation: newVal.use_local_confirmation || false,
          };
        }
      },
      { immediate: true, deep: true }
    );

    const onSubmit = async () => {
      if (!props.serverId) {
          toast({
            component: ToastificationContent,
            props: {
              title: "Server ID not found",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
      }

      const payload = {
          id_camera_proxy: itemData.value.camera,
          id_server: props.serverId,
          fire_threshold: itemData.value.fire_threshold ? parseFloat(itemData.value.fire_threshold) : null,
          smoke_threshold: itemData.value.smoke_threshold ? parseFloat(itemData.value.smoke_threshold) : null,
          use_local_confirmation: itemData.value.use_local_confirmation || false,
      };

      // En modo edición, agregar el status actual
      if (!props.isAdd) {
        payload.status = props.itemEdit.on_server_status;
      }

      const action = props.isAdd ? "add" : "updateStatus";
      store.dispatch(`ai_analytics_servers/devices/${action}`, payload)
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: String(response.message),
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: String(response.message),
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((error) => {
          toast({
            component: ToastificationContent,
            props: {
              title: String(error.response?.data?.message || "Error al procesar la solicitud"),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);

    return {
      itemData,
      onSubmit,
      refFormObserver,
      getValidationState,
      resetForm,
    };
  },
};
</script>
<style scoped>
  body.dark-layout .custom-range {
    background-color: var(--third-dark) !important;
  }
</style>
